import React, { useMemo, useState } from "react";
import { Group, Select, Skeleton, Stack, Text, TextInput } from "@mantine/core";
import { Alert, Badge, Button, ConfirmModal, PageHeader, SectionCard } from "@xplatform/design-system";
import { IconArrowLeft, IconEdit, IconGitCompare, IconTrash } from "@tabler/icons-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ConfigWarning, ImportReport } from "@models/mappingConfig";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import {
  useActivateVersion,
  useActiveVersion,
  useConfigDiff,
  useConfigVersion,
  useDeleteDraft,
} from "@hooks/mappingConfig";
import { ApiErrorAlert, ConfigDiffView, ConflictModal, ImportReportView, WarningsPanel } from "@features/MappingConfig";
import { errorCodeOf, isConflict, mappingConfigRoutes, warningsOf } from "@utils/mappingConfig";

type AgainstMode = "active" | "base" | "version";

/**
 * Review of one version before it goes live: the diff (against the active
 * version by default), the validator warnings and Approve. The person who
 * made the draft approves it (no four-eyes rule); Approve stays off while a
 * blocking warning remains or a warning is not acknowledged. Approving an
 * archived version is the rollback.
 */
export const MappingConfigReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { service = "", pluginId = "", versionId = "" } = useParams();
  const { canManageMappingConfig: canManage } = useAdminPermissions();
  const importReport = (location.state as { importReport?: ImportReport } | null)?.importReport ?? null;

  const [againstMode, setAgainstMode] = useState<AgainstMode>("active");
  const [otherVersion, setOtherVersion] = useState("");
  const against = againstMode === "version" ? otherVersion.trim() : againstMode;

  const version = useConfigVersion(service, pluginId, versionId);
  const active = useActiveVersion(service, pluginId);
  const diff = useConfigDiff(service, pluginId, versionId, against);
  const activate = useActivateVersion(service, pluginId);
  const deleteDraft = useDeleteDraft(service, pluginId);

  const [serverWarnings, setServerWarnings] = useState<ConfigWarning[] | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const warnings = useMemo(() => serverWarnings ?? diff.data?.warnings ?? [], [serverWarnings, diff.data]);
  const status = version.data?.status;
  const isDraft = status === "draft";
  const isActive = status === "active";

  const approve = async (acknowledged: string[]) => {
    setActionError(null);
    try {
      await activate.run(versionId, {
        expected_active_version_id: active.data?.version_id ?? null,
        acknowledged_warnings: acknowledged,
      });
      navigate(mappingConfigRoutes.plugin(service, pluginId));
    } catch (err) {
      if (isConflict(err)) {
        setConflict(errorCodeOf(err) ?? "active_changed");
        return;
      }
      const fresh = warningsOf(err);
      if (fresh) setServerWarnings(fresh);
      setActionError(err);
    }
  };

  const reload = () => {
    setConflict(null);
    setServerWarnings(null);
    setActionError(null);
    version.reload();
    active.reload();
    diff.reload();
  };

  const remove = async () => {
    try {
      await deleteDraft.run(versionId);
      navigate(mappingConfigRoutes.plugin(service, pluginId, "drafts"));
    } catch (err) {
      setConfirmDelete(false);
      setActionError(err);
    }
  };

  return (
    <Stack gap="md">
      <Group>
        <Button variant="subtle" size="sm" leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate(mappingConfigRoutes.plugin(service, pluginId, "history"))}>
          {t("mappingConfig.backToPlugin")}
        </Button>
      </Group>
      <PageHeader
        title={t("mappingConfig.review.title")}
        description={<Text span ff="monospace" size="sm">{`${service} / ${pluginId} / ${versionId}`}</Text>}
        icon={<IconGitCompare size={28} />}
        actions={
          <Group gap="sm">
            {status && <Badge variant="light">{status}</Badge>}
            {canManage && isDraft && (
              <>
                <Button variant="light" leftSection={<IconEdit size={16} />}
                        onClick={() => navigate(mappingConfigRoutes.edit(service, pluginId, versionId))}>
                  {t("mappingConfig.actions.edit")}
                </Button>
                <Button variant="light" color="red" leftSection={<IconTrash size={16} />} onClick={() => setConfirmDelete(true)}>
                  {t("mappingConfig.actions.deleteDraft")}
                </Button>
              </>
            )}
          </Group>
        }
      />

      {version.error && <Alert color="red" title={t("mappingConfig.loadFailed")}>{version.error}</Alert>}
      {version.data?.comment && <Text size="sm" c="dimmed">{version.data.comment}</Text>}
      {importReport && <ImportReportView report={importReport} />}

      <SectionCard title={t("mappingConfig.review.warnings")}>
        {diff.loading && !diff.data ? (
          <Skeleton h={64} />
        ) : (
          <WarningsPanel
            warnings={warnings}
            onApprove={canManage && !isActive ? approve : undefined}
            approveLoading={activate.loading}
            approveDisabled={!version.data || !!diff.error}
          />
        )}
        {status === "archived" && canManage && (
          <Text size="xs" c="dimmed" mt="xs">{t("mappingConfig.review.rollbackHint")}</Text>
        )}
      </SectionCard>

      {actionError !== null && <ApiErrorAlert error={actionError} />}

      <SectionCard
        title={t("mappingConfig.review.diff")}
        actions={
          <Group gap="sm">
            <Select
              size="xs"
              aria-label={t("mappingConfig.review.against")}
              value={againstMode}
              onChange={(v) => setAgainstMode((v as AgainstMode) ?? "active")}
              data={[
                { value: "active", label: t("mappingConfig.review.againstActive") },
                { value: "base", label: t("mappingConfig.review.againstBase") },
                { value: "version", label: t("mappingConfig.review.againstVersion") },
              ]}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true, portalProps: { target: "#admin-portal-root" } }}
              w={200}
            />
            {againstMode === "version" && (
              <TextInput size="xs" placeholder="v#..." value={otherVersion} onChange={(e) => setOtherVersion(e.currentTarget.value)} w={240} />
            )}
          </Group>
        }
      >
        {diff.error && <Alert color="red" title={t("mappingConfig.loadFailed")}>{diff.error}</Alert>}
        {diff.loading && !diff.data ? (
          <Stack gap="sm">{Array.from({ length: 3 }, (_, i) => <Skeleton key={i} h={48} />)}</Stack>
        ) : diff.data ? (
          <Stack gap="sm">
            <Text size="xs" c="dimmed" ff="monospace">
              {t("mappingConfig.review.fromTo", { from: diff.data.from_version_id ?? "-", to: diff.data.to_version_id })}
            </Text>
            <ConfigDiffView diff={diff.data} />
          </Stack>
        ) : null}
      </SectionCard>

      <ConflictModal opened={conflict !== null} code={conflict} onClose={() => setConflict(null)} onReload={reload} />

      <ConfirmModal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        title={t("mappingConfig.deleteDraft.title")}
        message={t("mappingConfig.deleteDraft.body", { id: versionId })}
        confirmLabel={t("mappingConfig.actions.delete")}
        cancelLabel={t("common.cancel")}
        loading={deleteDraft.loading}
        portalProps={{ target: "#admin-portal-root" }}
        danger
      />
    </Stack>
  );
};
